// Import constants for type definitions
import type { ANALYTICS_METRIC_IDS } from '../constants/analytics.constants';
import type { CHART_DISPLAY_TYPES } from '@/components/core-components/StanCharts';
import type { DateRangeType } from '@/lib/constants/dateFormats';

/**
 * Chart type constants to avoid magic strings
 */
export const CHART_TYPES = {
    VIEWS: 'views',
    REVENUE: 'revenue',
    LEADS: 'leads',
    PAGE_CONVERSION: 'page-conversion',
    PAGE_TRAFFIC_BREAKDOWN: 'page-traffic-breakdown',
    ORDERS: 'orders',
    TRAFFIC_SOURCES: 'traffic-sources',
    PAGE_TRAFFIC: 'page-traffic',
    COMPLEX_PAGE_TRAFFIC_BREAKDOWN: 'complex-page-traffic-breakdown',
} as const;

export interface DateRangeObject {
    startDate: Date | undefined;
    endDate: Date | undefined;
    rangeType: DateRangeType | undefined;
}

export interface ChartData {
    labels: string[];
    values: Record<string, number[]> | number[];
    total_current: number;
    ratio: number;
    totals?: Record<string, number>;
    pages_route_data?: Record<string, PageRouteData>;
}

export interface FetchChartDataParams {
    chartType: ChartType;
    dayRange: number;
    endDate: number;
    periodLength?: number;
    showSumSeries?: boolean;
}

export interface PageRouteData {
    store_type: string;
    store_id: string;
    page_id: string;
    page_type: string;
}

export interface AnalyticsCardMetric {
    title: string;
    value: number;
    trend: number;
    icon: string;
    color: string;
}

// Create proper types from constants
export type ChartDisplayType = (typeof CHART_DISPLAY_TYPES)[keyof typeof CHART_DISPLAY_TYPES];
export type AnalyticsMetricId = (typeof ANALYTICS_METRIC_IDS)[keyof typeof ANALYTICS_METRIC_IDS];

export interface AnalyticsChartConfig {
    color: string;
    decimals: number;
    name: string;
    chartType: ChartDisplayType;
    id: AnalyticsMetricId;
    stacked: boolean;
}

export interface ProductAnalyticsRow {
    product: string;
    views: number;
    orders: number;
    conversion: number;
    revenue: number;
}

/**
 * Chart type derived from CHART_TYPES constant for type safety
 */
export type ChartType = (typeof CHART_TYPES)[keyof typeof CHART_TYPES];

// Component Props Interfaces
export interface AnalyticsMetricsSectionProps {
    dateRange: DateRangeObject;
    initialChart?: ChartType;
}

export interface AnalyticsDateRangePickerProps {
    onRangeSelected: (range: DateRangeObject) => void;
}

export interface ProductAnalyticsTableProps {
    dateRange: DateRangeObject;
}

export interface TrafficSourcesChartProps {
    dateRange: DateRangeObject;
}

export interface PageTrafficChartProps {
    dateRange: DateRangeObject;
}

export interface AnalyticsMetricCardProps {
    metric: AnalyticsCardMetric;
    active: boolean;
    loading: boolean;
    onClick: () => void;
    className?: string;
    prefix?: string;
    highlightColor: string;
    keepStylingSameOnMobile?: boolean;
}
